import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';

import {
	useConstantStore,
	useFilterStore,
	useLanguageStore,
	useRoutingStore,
	useSearchSuggesterStore,
	useUserStore,
} from '@/helpers/composables/useStores';

import LoginMenuButton from '@/components/nav/LoginMenuButton.vue';
import LoginMenuButtonNew from '@/components/nav/LoginMenuButtonNew.vue';
import SettingsButton from '@/components/nav/static-pages/SettingsButton.vue';
import SearchSuggesterBar from '@/components/search/SearchSuggesterBar.vue';

import { CollectionType } from '@/enums/collection-type';
import { OnboardingStatus } from '@/enums/onboarding-status';
import { isSportSupported } from '@/enums/sports';
import type { FilterCollection } from '@/stores/filter.store';

import { getRouteName, getUrlParamsFromFilterState } from '@/helpers/state-helper';
import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { isEditorialLocale } from '@/routing/editorial';
import { faBookmark } from '@fortawesome/free-solid-svg-icons';


export default /*#__PURE__*/_defineComponent({
  __name: 'AppHeader',
  emits: ['tabChange'],
  setup(__props, { emit: emits }) {

const FreemiumFilterBadge = () =>
	import(/* webpackChunkName: "freemium-filter-badge" */ '@/components/filter-bar/FreemiumFilterBadge.vue');


const { subgenresList, providersByShortName, genresByShortName } = useConstantStore();
const { filters } = useFilterStore();
const { webLocale } = useLanguageStore();
const { activeRoute, urlMetadataPromise, lastActiveListsCollectionType, lastActiveCollectionType } = useRoutingStore();
const { isSearchSuggesterOpen, setSearchSuggesterVisibility } = useSearchSuggesterStore();
const { onboardingStatus, isLoggedIn, isPremium } = useUserStore();

const isNewTab = computed(() => {
	const newPageRoutes = ['app.titles.new', 'app.titles.upcoming', 'app.titles.pricedrops'];
	return newPageRoutes.find(route => (activeRoute.value.name || '').startsWith(route));
});

const isNetflixStatisticPage = computed(() => activeRoute.value.name === 'app.landingpage.statistics');

const isHome = computed(() => activeRoute.value.name === 'app.home');

const isGlobal = computed(() => onboardingStatus.value === OnboardingStatus.GLOBAL);

const isUnsupportedCountry = computed(() => onboardingStatus.value === OnboardingStatus.UNSUPPORTED_COUNTRY);

const isStreamingCharts = computed(() => activeRoute.value.name === 'app.streaming-charts');

const logoSrc = computed(() => {
	const file = `JustWatch-logo-${isGlobal.value ? 'large' : 'small'}`;
	return {
		png: `/${ASSETS_DIR}/img/logo/${file}.png`,
		webp: `/${ASSETS_DIR}/img/logo/${file}.webp`,
	};
});

const hasSports = computed(() => isSportSupported(webLocale.value));

const isGuideLocale = computed(() => isEditorialLocale(webLocale.value));

const listsCollectionTypeToNavigate = computed(() => {
	if (lastActiveListsCollectionType.value) {
		return lastActiveListsCollectionType.value;
	}

	return isLoggedIn.value ? CollectionType.MY_LISTS : CollectionType.PUBLIC_LISTS;
});

/**
 * with these parameters we can always link to the correct url for each collectionType page.
 * in some cases we want to overwrite the currentFilters of the passed CollectionType, that's what `filterOverwrite` handles.
 */
function routeParams(collectionType: CollectionType, filterOverwrite?: FilterCollection): any {
	const isNewPageCollectionType = [
		CollectionType.COMING_SOON,
		CollectionType.LEAVING_SOON,
		CollectionType.PRICEDROPS,
	].includes(collectionType);

	const filterCollectionType = isNewPageCollectionType ? CollectionType.NEW : collectionType;
	const _filters = filterOverwrite ?? filters.value(filterCollectionType);
	const routeName = getRouteName(collectionType, _filters, subgenresList.value);
	const params = getUrlParamsFromFilterState(
		routeName,
		_filters,
		providersByShortName.value,
		genresByShortName.value,
		collectionType,
		activeRoute.value.query,
		subgenresList.value
	);

	// Note: list_id query param needs to be deleted when navigating to the Lists tab root level, since we are
	// using it on Lists tab to determinate if list details page is opened
	if (params.query.list_id) {
		delete params.query.list_id;
	}

	return { name: routeName, ...params };
}

async function onTabChange(newTab: string) {
	emits('tabChange');
	await urlMetadataPromise.value;
	const pageTypeContext = TrackingHelper.getPageTypeContext(true);
	trackEvent('tabchange', newTab, [pageTypeContext]);
	return newTab;
}

function trackEvent(action: string, label?: string, contexts: SnowplowContext[] = []) {
	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action,
			label,
		},
		contexts
	);
}

function onOverlayClick() {
	setSearchSuggesterVisibility(false);
}

return { __sfc: true,FreemiumFilterBadge, emits, subgenresList, providersByShortName, genresByShortName, filters, webLocale, activeRoute, urlMetadataPromise, lastActiveListsCollectionType, lastActiveCollectionType, isSearchSuggesterOpen, setSearchSuggesterVisibility, onboardingStatus, isLoggedIn, isPremium, isNewTab, isNetflixStatisticPage, isHome, isGlobal, isUnsupportedCountry, isStreamingCharts, logoSrc, hasSports, isGuideLocale, listsCollectionTypeToNavigate, routeParams, onTabChange, trackEvent, onOverlayClick, LoginMenuButton, LoginMenuButtonNew, SettingsButton, SearchSuggesterBar, CollectionType, faBookmark }
}

})